// POSTFACH-LOESCHEN-001 — echtes Partner-Postfach statt Seed-Attrappe.
// Liste kommt aus GET /partners/support/inbox (kind: 'conversation' | 'notification');
// Löschweg ist POST /partners/support/inbox/delete (einzeln, Mehrfachauswahl,
// „Alle Hinweise entfernen" serverseitig — auch nicht geladene Einträge).
// Regel der Karte: Nur Hinweise und GESCHLOSSENE Gespräche sind entfernbar. Offene
// Gespräche bekommen keinen Papierkorb und im Auswahlmodus kein aktivierbares Häkchen.
// Abgelehnte Einträge (refused) werden sichtbar gemeldet, nie still verschluckt.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Bell, Inbox as InboxIcon, Lock, MessageCircle, Send, Trash2 } from 'lucide-react';
import api from '../services/api';
import { Badge, Btn, C, Empty, ErrorBox, R, Skeleton } from '../ui/kit';
import { waitForTypingDelay } from '../ui/typing';
import { datumKurzMitZeit } from '../lib/zeit';

const keyOf = (item) => `${item.kind}:${item.id}`;
// Entfernbar = Hinweis ODER vom Admin gelöstes (closed) Gespräch — exakt die Backend-Regel.
const isRemovable = (item) => item.kind === 'notification'
  || (item.kind === 'conversation' && item.status === 'closed');

// ZEIT-HELFER-001: created_at kommt als SQLite-UTC („YYYY-MM-DD HH:MM:SS"); lib/zeit liest es
// als UTC und zeigt Berlin. Leer/ungültig bleibt wie bisher ein leerer Text.
const fmt = (d) => (d ? datumKurzMitZeit(d) : '');

// refused je Grund zusammenfassen — der Nutzer muss merken, dass Einträge geblieben sind.
function refusedSummary(refused) {
  const open = refused.filter((r) => r.reason === 'conversation_open').length;
  const missing = refused.length - open;
  const parts = [];
  if (open) parts.push(open === 1 ? 'ein Gespräch ist noch offen und bleibt im Postfach' : `${open} Gespräche sind noch offen und bleiben im Postfach`);
  if (missing) parts.push(missing === 1 ? 'ein Eintrag wurde nicht gefunden' : `${missing} Einträge wurden nicht gefunden`);
  return `${refused.length === 1 ? 'Ein Eintrag wurde' : `${refused.length} Einträge wurden`} nicht entfernt: ${parts.join('; ')}.`;
}

export default function Inbox() {
  const [items, setItems] = useState(null);          // null = erste Ladung läuft
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(null);    // Listeneintrag im Lesebereich
  const [detail, setDetail] = useState(null);        // Gesprächsdetail { loading, conversation, messages, error }
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [removing, setRemoving] = useState(false);
  const [composing, setComposing] = useState(false);
  const [supportDraft, setSupportDraft] = useState('');
  const [supportTurns, setSupportTurns] = useState([]);
  const [sendingSupport, setSendingSupport] = useState(false);
  const [supportCaseCreated, setSupportCaseCreated] = useState(false);
  const [createdConversationId, setCreatedConversationId] = useState(null);
  const readReported = useRef(false);
  const supportRequestRef = useRef(0);
  const detailPaneRef = useRef(null);

  const revealDetailPaneOnNarrowScreen = () => {
    if (typeof window === 'undefined' || !window.matchMedia?.('(max-width: 780px)').matches) return;
    const reveal = () => detailPaneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(reveal);
    else reveal();
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get('/partners/support/inbox');
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
      setLoadError(false);
      return list;
    } catch {
      setLoadError(true);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const list = await load();
      // Bestehende Lesenaht (alles als gelesen quittieren) — einmal je Besuch,
      // die Ungelesen-Punkte der aktuellen Ansicht bleiben dabei sichtbar.
      if (list && !readReported.current) {
        readReported.current = true;
        try { await api.post('/partners/support/inbox/read'); } catch { /* nicht kritisch */ }
      }
    })();
  }, [load]);

  const loadConversation = useCallback(async (conversationId) => {
    setDetail({ loading: true });
    try {
      const res = await api.get(`/partners/support/conversations/${conversationId}`);
      setDetail({ loading: false, conversation: res.data?.conversation || null, messages: res.data?.messages || [] });
      setItems((list) => (list || []).map((x) => (x.kind === 'conversation' && x.id === conversationId ? { ...x, unread: false } : x)));
    } catch {
      setDetail({ loading: false, error: true, conversationId });
    }
  }, []);

  const toggleSelect = (item) => {
    if (!isRemovable(item)) return; // offene Gespräche sind nicht wählbar
    const key = keyOf(item);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const openItem = (item) => {
    if (selectMode) { toggleSelect(item); return; }
    supportRequestRef.current += 1;
    setSendingSupport(false);
    setComposing(false);
    setSelected(item);
    setReply('');
    revealDetailPaneOnNarrowScreen();
    if (item.kind === 'conversation') {
      loadConversation(item.id);
    } else {
      setDetail(null);
      setItems((list) => (list || []).map((x) => (x.kind === 'notification' && x.id === item.id ? { ...x, unread: false } : x)));
    }
  };

  // Zentraler Löschaufruf: Ergebnis melden (auch refused), danach Liste neu vom Server.
  const performRemove = async (body) => {
    if (removing) return;
    setRemoving(true);
    try {
      const res = await api.post('/partners/support/inbox/delete', body);
      const deleted = Number(res.data?.deleted_notifications || 0);
      const removed = Number(res.data?.removed_conversations || 0);
      const refused = Array.isArray(res.data?.refused) ? res.data.refused : [];
      const total = deleted + removed;
      if (total) toast.success(total === 1 ? 'Eintrag entfernt.' : `${total} Einträge entfernt.`);
      if (refused.length) toast.error(refusedSummary(refused));
      const fresh = await load();
      if (Array.isArray(fresh)) {
        setSelectedKeys((prev) => {
          const next = new Set();
          fresh.forEach((i) => { if (prev.has(keyOf(i))) next.add(keyOf(i)); });
          return next;
        });
        setSelected((prev) => {
          if (prev && fresh.some((i) => keyOf(i) === keyOf(prev))) return prev;
          setDetail(null);
          setReply('');
          return null;
        });
      }
    } catch {
      toast.error('Entfernen fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setRemoving(false);
    }
  };

  // Einzelner Papierkorb: eine Rückfrage genügt (Portal-Idiom, vgl. Promotions).
  const removeOne = (item) => {
    const question = item.kind === 'notification'
      ? 'Diesen Hinweis endgültig löschen?'
      : 'Dieses gelöste Gespräch aus dem Postfach entfernen?';
    if (!window.confirm(question)) return;
    performRemove(item.kind === 'notification'
      ? { notification_ids: [item.id] }
      : { conversation_ids: [item.id] });
  };

  const removeSelected = () => {
    const chosen = (items || []).filter((i) => selectedKeys.has(keyOf(i)) && isRemovable(i));
    const notificationIds = chosen.filter((i) => i.kind === 'notification').map((i) => i.id);
    const conversationIds = chosen.filter((i) => i.kind === 'conversation').map((i) => i.id);
    const total = notificationIds.length + conversationIds.length;
    if (!total) return;
    if (!window.confirm(`${total === 1 ? 'Einen Eintrag' : `${total} Einträge`} entfernen? Hinweise werden endgültig gelöscht, gelöste Gespräche aus dem Postfach genommen.`)) return;
    const body = {};
    if (notificationIds.length) body.notification_ids = notificationIds;
    if (conversationIds.length) body.conversation_ids = conversationIds;
    setSelectMode(false);
    setSelectedKeys(new Set());
    performRemove(body);
  };

  // „Alle Hinweise entfernen" läuft serverseitig — die Rückfrage sagt ausdrücklich,
  // dass ALLE Hinweise betroffen sind, auch die hier nicht angezeigten. Gespräche nie.
  const removeAllNotifications = () => {
    if (!window.confirm('Wirklich ALLE Hinweise entfernen?\n\nDas löscht sämtliche Benachrichtigungen deines Postfachs endgültig — auch ältere, die hier nicht angezeigt werden. Gespräche bleiben unberührt.')) return;
    performRemove({ all_notifications: true });
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending || !selected || selected.kind !== 'conversation') return;
    setSending(true);
    try {
      await api.post(`/partners/support/conversations/${selected.id}/messages`, { message: text });
      setReply('');
      await loadConversation(selected.id);
    } catch {
      toast.error('Antwort konnte nicht gesendet werden.');
    } finally {
      setSending(false);
    }
  };

  const startSupportChat = () => {
    supportRequestRef.current += 1;
    setSelectMode(false);
    setSelectedKeys(new Set());
    setSelected(null);
    setDetail(null);
    setReply('');
    setSupportDraft('');
    setSupportTurns([]);
    setSupportCaseCreated(false);
    setCreatedConversationId(null);
    setSendingSupport(false);
    setComposing(true);
    revealDetailPaneOnNarrowScreen();
  };

  // Derselbe rollenbasierte Chat wie in den Apps. Die Partnerrolle kommt aus dem
  // authentifizierten Portal-Token, nicht aus einem vom Browser behaupteten Body-Feld.
  const sendSupportMessage = async () => {
    const message = supportDraft.trim();
    if (!message || sendingSupport || supportCaseCreated) return;
    const history = supportTurns.slice(-8).map(({ role, content }) => ({ role, content }));
    const requestId = ++supportRequestRef.current;
    // SUPPORT-TIPP-INDIKATOR-001: Absendezeitpunkt merken — die Antwort wird frühestens
    // nach der Mindestanzeige des Tipp-Indikators sichtbar (Details in ../ui/typing.js).
    const startedAt = Date.now();
    setSendingSupport(true);
    try {
      const res = await api.post('/support-chat/message', { message, history });
      if (requestId !== supportRequestRef.current) return;
      const answer = typeof res.data?.reply === 'string' ? res.data.reply.trim() : '';
      if (!answer) throw new Error('support_reply_missing');
      // Rest-Wartezeit NUR im Client, zwischen Eintreffen und Anzeigen; bei langsamer
      // Antwort ist sie 0. Danach erneut prüfen, ob inzwischen ein neuer Chat läuft.
      await waitForTypingDelay(startedAt);
      if (requestId !== supportRequestRef.current) return;
      setSupportTurns((turns) => [
        ...turns,
        { role: 'user', content: message },
        { role: 'assistant', content: answer },
      ]);
      setSupportDraft('');

      if (res.data?.ticketCreated) {
        const conversationId = typeof res.data?.conversation_id === 'string'
          ? res.data.conversation_id : null;
        setSupportCaseCreated(true);
        setCreatedConversationId(conversationId);
        await load();
        toast.success('Support-Fall angelegt. Antworten erscheinen in deinem Postfach.');
      } else if (res.data?.escalate && res.data?.ticketCreated === false) {
        toast.error('Der Fall konnte nicht angelegt werden. Bitte nutze den im Chat genannten Kontaktweg.');
      }
    } catch (error) {
      if (requestId !== supportRequestRef.current) return;
      // Auch Fehlermeldungen erst nach der Mindestanzeige — ein 0-ms-Fehler verrät den
      // Automaten genauso wie eine 0-ms-Antwort. (Nach dem !answer-Wurf ist die Rest-Zeit 0.)
      await waitForTypingDelay(startedAt);
      if (requestId !== supportRequestRef.current) return;
      const rateLimited = error?.response?.status === 429;
      toast.error(rateLimited
        ? 'Zu viele Nachrichten — bitte kurz warten.'
        : 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      if (requestId === supportRequestRef.current) setSendingSupport(false);
    }
  };

  const openCreatedConversation = async () => {
    if (!createdConversationId) return;
    const fresh = await load();
    const item = Array.isArray(fresh)
      ? fresh.find((entry) => entry.kind === 'conversation' && entry.id === createdConversationId)
      : null;
    if (!item) {
      toast.error('Der neue Fall ist im Postfach noch nicht verfügbar. Bitte erneut versuchen.');
      return;
    }
    setComposing(false);
    setSelected(item);
    setReply('');
    await loadConversation(item.id);
  };

  const returnToInbox = async () => {
    await load();
    setComposing(false);
    setSelected(null);
    setDetail(null);
  };

  const hasNotifications = (items || []).some((i) => i.kind === 'notification');
  const selectedCount = selectedKeys.size;

  const renderRow = (item) => {
    const removable = isRemovable(item);
    const checked = selectedKeys.has(keyOf(item));
    const Icon = item.kind === 'conversation' ? MessageCircle : Bell;
    return (
      <div
        key={keyOf(item)}
        data-testid={`inbox-row-${keyOf(item)}`}
        onClick={() => openItem(item)}
        style={{
          padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
          background: !selectMode && selected && keyOf(selected) === keyOf(item) ? '#f9fafb' : '#fff',
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}
      >
        {selectMode ? (
          removable ? (
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggleSelect(item)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Auswählen: ${item.title}`}
              style={{ accentColor: C.brand, width: 16, height: 16, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
            />
          ) : (
            // Offen = nicht entfernbar: bewusst KEIN Häkchen, nur ein stilles Schloss.
            <Lock size={16} color={C.faint} aria-label="Offenes Gespräch — nicht entfernbar" style={{ flexShrink: 0, marginTop: 2 }} />
          )
        ) : (
          <Icon size={18} color={item.unread ? C.brand : C.faint} style={{ flexShrink: 0, marginTop: 2 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: item.unread ? 800 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{item.title}</span>
            {item.kind === 'conversation' && (
              item.status === 'closed'
                ? <Badge color={C.muted}>Gelöst</Badge>
                : <Badge color={C.brand}>Offen</Badge>
            )}
          </div>
          {item.snippet ? (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.snippet}</div>
          ) : null}
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{fmt(item.created_at)}</div>
        </div>
        {!selectMode && removable && (
          <button
            type="button"
            aria-label={`Löschen: ${item.title}`}
            title={item.kind === 'notification' ? 'Hinweis löschen' : 'Gelöstes Gespräch entfernen'}
            disabled={removing}
            onClick={(e) => { e.stopPropagation(); removeOne(item); }}
            style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: C.faint, flexShrink: 0, borderRadius: R.sm }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    );
  };

  const renderDetail = () => {
    if (composing) {
      return (
        <div className="pp-support-compose">
          <div className="pp-support-compose-head">
            <span className="pp-support-compose-icon"><MessageCircle size={20} /></span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Support-Chat</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 3 }}>
                Schreib dein Anliegen. Der Assistent antwortet direkt; wenn das Team übernimmt,
                erscheint der Fall in diesem Postfach.
              </div>
            </div>
          </div>
          <div className="pp-support-turns" aria-live="polite">
            {supportTurns.length === 0 && (
              <div className="pp-support-empty">Wobei können wir dir helfen?</div>
            )}
            {supportTurns.map((turn, index) => (
              <div key={`${turn.role}-${index}`} className={`pp-support-turn is-${turn.role}`}>
                <span>{turn.role === 'assistant' ? 'VOOGO Support' : 'Du'}</span>
                <div>{turn.content}</div>
              </div>
            ))}
            {/* Tipp-Indikator: läuft, solange die Anfrage unterwegs ist bzw. die
                Mindestanzeige (ui/typing.js) noch nicht abgelaufen ist. */}
            {sendingSupport && (
              <div className="pp-support-turn is-assistant" role="status" aria-label="Assistent schreibt">
                <span>VOOGO Support</span>
                <div className="pp-typing" aria-hidden="true">
                  <span /><span /><span />
                </div>
              </div>
            )}
          </div>
          {supportCaseCreated && (
            <div className="pp-support-created" role="status">
              <span>Dein Support-Fall ist angelegt. Weitere Nachrichten gehören jetzt in das bestehende Gespräch.</span>
              <Btn variant="primary" onClick={createdConversationId ? openCreatedConversation : returnToInbox}>
                {createdConversationId ? 'Im Postfach öffnen' : 'Zum Postfach'}
              </Btn>
            </div>
          )}
          {!supportCaseCreated && (
            <div className="pp-support-input">
              <textarea
                value={supportDraft}
                onChange={(event) => setSupportDraft(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault();
                    sendSupportMessage();
                  }
                }}
                placeholder="Nachricht schreiben…"
                rows={4}
                maxLength={1000}
                disabled={sendingSupport}
                aria-label="Nachricht an den Support"
              />
              <div className="pp-support-input-foot">
                <small>{supportDraft.length}/1000 · Senden mit ⌘/Strg + Enter</small>
                <Btn variant="primary" disabled={sendingSupport || !supportDraft.trim()} onClick={sendSupportMessage}>
                  <Send size={14} /> {sendingSupport ? 'Wird gesendet…' : 'Nachricht senden'}
                </Btn>
              </div>
            </div>
          )}
        </div>
      );
    }
    if (!selected) {
      return (
        <div className="pp-support-entry">
          <span className="pp-support-compose-icon"><MessageCircle size={22} /></span>
          <strong>Support erreichen</strong>
          <p>Schreib VOOGO direkt oder wähle links ein bestehendes Gespräch.</p>
          <Btn variant="primary" onClick={startSupportChat}><MessageCircle size={14} /> Support kontaktieren</Btn>
        </div>
      );
    }
    if (selected.kind === 'notification') {
      return (
        <>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{selected.title}</div>
          <div style={{ fontSize: 13, color: C.faint, marginBottom: 20 }}>VOOGO · {fmt(selected.created_at)}</div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{selected.message || selected.snippet || '—'}</div>
          <div style={{ marginTop: 24 }}>
            <Btn variant="secondary" disabled={removing} onClick={() => removeOne(selected)} style={{ color: '#b91c1c' }}>
              <Trash2 size={14} /> Hinweis löschen
            </Btn>
          </div>
        </>
      );
    }
    // Gespräch
    if (detail?.loading) {
      return (<div><Skeleton w={220} h={20} /><div style={{ height: 14 }} /><Skeleton w="100%" h={60} r={10} /><div style={{ height: 10 }} /><Skeleton w="80%" h={60} r={10} /></div>);
    }
    if (detail?.error) {
      return <ErrorBox text="Gespräch konnte nicht geladen werden." onRetry={() => loadConversation(selected.id)} />;
    }
    const conversation = detail?.conversation || null;
    const messages = detail?.messages || [];
    const closed = (conversation?.status || selected.status) === 'closed';
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1, minWidth: 0 }}>{conversation?.subject || selected.title}</div>
          {closed ? <Badge color={C.muted}>Gelöst</Badge> : <Badge color={C.brand}>Offen</Badge>}
        </div>
        <div style={{ fontSize: 13, color: C.faint, marginBottom: 18 }}>Support-Gespräch · {fmt(selected.created_at)}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {messages.map((m) => (
            <div key={m.id} style={{ alignSelf: m.sender_role === 'admin' ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
              <div style={{ fontSize: 12, color: C.faint, marginBottom: 2, textAlign: m.sender_role === 'admin' ? 'left' : 'right' }}>
                {m.sender_role === 'admin' ? 'VOOGO Support' : 'Du'} · {fmt(m.created_at)}
              </div>
              <div style={{ background: m.sender_role === 'admin' ? '#f3f4f6' : C.brandTint, borderRadius: R.md, padding: '10px 12px', fontSize: 14, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.message}</div>
            </div>
          ))}
          {!messages.length && <div style={{ fontSize: 13, color: C.faint }}>Noch keine Nachrichten.</div>}
        </div>
        {closed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
            <div style={{ fontSize: 13, color: C.muted, flex: 1 }}>Dieses Gespräch wurde vom Support gelöst.</div>
            <Btn variant="secondary" disabled={removing} onClick={() => removeOne(selected)} style={{ color: '#b91c1c' }}>
              <Trash2 size={14} /> Aus dem Postfach entfernen
            </Btn>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Antwort schreiben…"
              rows={2}
              aria-label="Antwort schreiben"
              style={{ flex: 1, resize: 'vertical', border: `1px solid ${C.line}`, borderRadius: R.md, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit' }}
            />
            <Btn variant="primary" disabled={sending || !reply.trim()} onClick={sendReply} style={{ alignSelf: 'flex-end' }}>
              <Send size={14} /> Senden
            </Btn>
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -1, marginBottom: 6 }}>Support & Postfach</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: 0 }}>VOOGO anschreiben und bestehende Gespräche oder Hinweise lesen</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="primary" onClick={startSupportChat}>
            <MessageCircle size={14} /> Neue Support-Nachricht
          </Btn>
          {Array.isArray(items) && items.length > 0 && (
            selectMode ? (
              <>
              <Btn variant="danger" disabled={removing || selectedCount === 0} onClick={removeSelected}>
                <Trash2 size={14} /> Entfernen ({selectedCount})
              </Btn>
              <Btn variant="ghost" onClick={() => { setSelectMode(false); setSelectedKeys(new Set()); }}>Abbrechen</Btn>
              </>
            ) : (
              <>
              <Btn variant="secondary" onClick={() => { setSelectMode(true); setSelectedKeys(new Set()); setSelected(null); setDetail(null); }}>Auswählen</Btn>
              {hasNotifications && (
                <Btn variant="secondary" disabled={removing} onClick={removeAllNotifications} style={{ color: '#b91c1c' }}>
                  <Trash2 size={14} /> Alle Hinweise entfernen
                </Btn>
              )}
              </>
            )
          )
          }
        </div>
      </div>

      {loadError && !Array.isArray(items) ? (
        <ErrorBox text="Postfach konnte nicht geladen werden." onRetry={load} />
      ) : (
        <div className="pp-inbox-grid" style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20, alignItems: 'start', border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ borderRight: `1px solid ${C.line}` }}>
            {items === null ? (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Skeleton w="90%" h={16} /><Skeleton w="70%" h={16} /><Skeleton w="80%" h={16} />
              </div>
            ) : items.length === 0 ? (
              <Empty icon={InboxIcon} title="Keine Nachrichten" hint="Hinweise und Support-Gespräche erscheinen hier." />
            ) : (
              items.map(renderRow)
            )}
          </div>
          <div ref={detailPaneRef} style={{ padding: 24, minHeight: 320 }}>
            {renderDetail()}
          </div>
        </div>
      )}
    </div>
  );
}
